import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";

export default function SearchOverlay({ open, onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 120);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setQuery("");
      setResults([]);
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/products", { params: { search: query, limit: 8 } });
        setResults(data);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [query]);

  const go = (id) => {
    onClose();
    navigate(`/products/${id}`);
  };

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="search-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[60] bg-white"
        >
          <div className="mx-auto flex h-full max-w-[1300px] flex-col px-5 md:px-10">
            <div className="flex h-20 items-center justify-between">
              <span className="text-[12px] font-bold uppercase tracking-[0.25em] text-gavra">GAVRA / Search</span>
              <button data-testid="close-search-btn" onClick={onClose} className="flex items-center gap-2 text-black hover:text-gavra transition-colors">
                <span className="text-[12px] font-bold uppercase tracking-[0.2em]">Close</span>
                <X className="h-6 w-6" />
              </button>
            </div>

            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 border-b-2 border-black pb-4"
            >
              <div className="flex items-center gap-4">
                <Search className="h-7 w-7 md:h-9 md:w-9 text-gavra shrink-0" strokeWidth={2.5} />
                <input
                  ref={inputRef}
                  data-testid="global-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by Product Name or SKU"
                  className="w-full bg-transparent font-heading text-3xl md:text-5xl font-extrabold uppercase tracking-tight text-black placeholder:text-black/20 outline-none"
                />
              </div>
            </motion.div>

            <div className="mt-8 flex-1 overflow-y-auto pb-10" data-lenis-prevent>
              {loading && <p className="text-sm uppercase tracking-widest text-black/40">Searching…</p>}
              {!loading && query && results.length === 0 && (
                <p data-testid="search-no-results" className="text-sm uppercase tracking-widest text-black/40">
                  No products found for “{query}”.
                </p>
              )}
              <div className="divide-y divide-black/10">
                {results.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`search-result-${p.sku}`}
                    onClick={() => go(p.id)}
                    className="group flex w-full items-center gap-5 py-5 text-left"
                  >
                    <div className="h-16 w-16 shrink-0 border border-black/10 bg-secondary overflow-hidden">
                      {p.image_url && <img src={p.image_url} alt={p.name} className="h-full w-full object-cover" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3">
                        <span data-testid="sku-badge" className="bg-gavra px-2 py-0.5 font-mono-hw text-[11px] font-bold text-white">
                          {p.sku}
                        </span>
                        <span className="font-mono-hw text-[11px] uppercase tracking-widest text-black/40">{p.category}</span>
                      </div>
                      <p className="mt-1 truncate font-heading text-xl font-bold text-black group-hover:text-gavra transition-colors">
                        {p.name}
                      </p>
                    </div>
                    <ArrowUpRight className="h-6 w-6 text-black/30 group-hover:text-gavra transition-colors" />
                  </button>
                ))}
              </div>
              {!query && (
                <p className="text-sm uppercase tracking-[0.2em] text-black/30">
                  Try “drum key”, “hoop”, or an SKU like “GV-CM-0080”.
                </p>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
